<template>
    <el-container style="height: calc(100vh - 80px)" class="bg-white">
        <el-header style="padding: 0px; height: auto; ">
            <!-- title部 -->
            <div class="h-12 p-4 shadow-md">システム設定 > センサー管理</div>

            <!-- 検索部 -->
            <div class="p-5">
                <div class="border border-gray-300 rounded-md p-4">
                    <el-form :model="searchForm" label-width="auto">
                        <div class="flex flex-col md:flex-row gap-x-4">
                            <el-form-item label="契約会社" class="md:w-80">
                                <CompanySelector v-model="searchForm.company_code" :isSearch=true :disabled="loginUserRole !== ROLES.SYSTEM"/>
                            </el-form-item>

                            <el-button class="w-24" type="primary"
                                @click="handleSearchButton">検索</el-button>
                        </div>
                    </el-form>

                    <div class="flex justify-end">
                        <el-button class="w-24 mr-4" type="primary"
                                @click="handleAddButton">新規登録</el-button>

                        <el-upload ref="upload" :on-change="handleImportButton"
                            :auto-upload="false" :show-file-list="false" accept=".csv">
                            <el-button class="w-30" type="primary">インポート</el-button>
                        </el-upload>
                    </div>
                </div>
            </div>
        </el-header>

        <!-- 主显示部 -->
        <el-main style="padding-block: 0px;" v-loading="loading">
            <!-- テーブル -->
            <div class="border border-gray-300 rounded-md p-4">
                <el-table :data="mTableData" :height="'calc(100vh - 380px)'">
                    <!-- <el-table-column label="編集（テスト）" width="160" align="center">
                        <template #default="scope">
                            <el-button type="primary" link @click="handleEditButton(scope.row)">
                                <el-icon>
                                    <Edit />
                                </el-icon>
                            </el-button>
                        </template>
                    </el-table-column> -->
                    <el-table-column prop="company_code" label="契約会社" width="160"/>
                    <el-table-column prop="sensor_type" label="センサー種別">
                        <template #default="scope">
                            <span>{{ scope.row.sensor_type === 'calera' ? '体温センサー' : '心拍センサー'}}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="address" label="センサーMAC"/>
                    <el-table-column prop="inuse" width="80" label="使用中" align="center">
                        <template #default="scope">
                            <span>{{ getFlag(scope.row.inuse) }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="削除" width="60" align="center">
                        <template #default="scope">
                            <el-button v-if="!scope.row.inuse" type="danger" link @click="handleDeleteButton(scope.row)">
                                <el-icon>
                                    <Delete />
                                </el-icon>
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>
            </div>

            <!-- ページネーション -->
            <div class="flex justify-end mt-2">
                <el-pagination v-model:current-page="mCurrentPage" v-model:page-size="mPageSize"
                    :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next, jumper" :total="mTotalRecords"
                    @size-change="handleSizeChange" @current-change="handleCurrentChange" />
            </div>
        </el-main>

        <el-footer style="height: 20px;"> </el-footer>
    </el-container>

    <!-- 新規登録・編集ダイアログ -->
    <el-dialog v-model="dialogFormVisible" :title="form.add_update === 'add' ? 'センサー新規登録' : 'センサー編集'" width="600px" align-center>
        <el-form :model="form" :rules="rules" ref="formRef" label-width="auto">
            <el-input v-model="form._id" hidden />

            <el-form-item label="所属会社" prop="company_code">
                <CompanySelector v-model="form.company_code" :disabled="loginUserRole !== ROLES.SYSTEM"/>
            </el-form-item>

            <el-form-item label="センサー種別" prop="sensor_type">
                <el-select v-model="form.sensor_type" placeholder="センサー種別をを選択してください">
                    <el-option label="体温センサー" value="calera" />
                    <el-option label="心拍センサー" value="hrm" />
                </el-select>
            </el-form-item>

            <el-form-item label="センサー MAC" prop="address">
                <el-input v-model="form.address" placeholder="センサー MACを入力" />
            </el-form-item>

        </el-form>
        <template #footer>
            <div class="dialog-footer">
                <el-button @click="dialogFormVisible = false">キャンセル</el-button>
                <el-button type="primary" @click="handleAddUpdateSubmit(formRef)">確認</el-button>
            </div>
        </template>
    </el-dialog>
</template>

<script lang="ts">
import axios, { AxiosResponse } from 'axios';
import { ElMessage, ElMessageBox, ElPagination, ElTable, ElTableColumn, ElUpload } from 'element-plus';
import { defineComponent, onMounted, ref, nextTick } from 'vue';
import type { UploadFile, FormInstance, FormRules } from 'element-plus';
import { Delete, /*Edit*/ } from '@element-plus/icons-vue';
import { ErrorCode, ResponseData, ResponseStatus } from '@/components/common/response';
import { getErrorMessage } from '@/components/common/errorMessage';
import { Operation } from '@/components/common/request';
import { SensorInfo } from '@/components/models/sensorInfo';
import { checkToken } from '@/components/common/auth';
import CompanySelector from '@/components/common/company-selector.vue';
import StringUtils from '@/components/common/StringUtils';
import { authService } from '@/services/authService';
import { ALL, ROLES } from '@/config/constants';

export default defineComponent({
    name: 'MntSensors',
    components: {
        CompanySelector,
        ElTable,
        ElTableColumn,
        ElPagination,
        ElUpload,
        Delete,
        // Edit,
    },
    setup() {
        const loginUserRole = authService.getUserInfo().user_role ?? '';
        const loginUserCompanyCode = authService.getUserInfo().company_code ?? '';

        const getFlag = (value: boolean) => {
            return StringUtils.getFlag(value);
        }

        const searchForm = ref<any>({
            company_code: loginUserRole === ROLES.SYSTEM ? ALL : loginUserCompanyCode,
        });

        const form = ref<SensorInfo>({
            add_update: Operation.unknow,
            _id: '',
            company_code: '',
            sensor_type: '',
            address: '',
        });

        const loading = ref<boolean>(true);
        const mTableData = ref<SensorInfo[]>([]);
        const mTotalPages = ref<number>(0);
        const mTotalRecords = ref<number>(0);
        const mCurrentPage = ref<number>(1);
        const mPageSize = ref<number>(10);

        const dialogFormVisible = ref<boolean>(false);


        const formRef = ref<FormInstance>()
        const rules = ref<FormRules>({
            company_code: [
                { required: true, message: '契約会社を選択してください', trigger: 'blur' },
            ],
            sensor_type: [
                { required: true, message: 'センサー種別を入力してください', trigger: 'blur' },
            ],
            address: [
                { required: true, message: 'センサーMACを入力してください', trigger: 'blur' },
                { pattern: /^[0-9A-Fa-f]{2}[:-]{0,1}[0-9A-Fa-f]{2}[:-]{0,1}[0-9A-Fa-f]{2}[:-]{0,1}[0-9A-Fa-f]{2}[:-]{0,1}[0-9A-Fa-f]{2}[:-]{0,1}[0-9A-Fa-f]{2}$/, message: '無効なMACアドレスです', trigger: 'blur' }
            ]
        })

        /***** マウント **************************/
        onMounted(() => {

            checkToken();

            nextTick(() => {
                getSensorList();
            });
        });

        /***** ボタン **************************/
        const handleSearchButton = () => {
            getSensorList();
        };

        const handleAddButton = () => {
            dialogFormVisible.value = true;
            nextTick(() => {
                formRef.value?.resetFields();
                form.value = {
                    add_update: Operation.add,
                    company_code: loginUserRole === ROLES.SYSTEM ? '' : loginUserCompanyCode,
                    sensor_type: '',
                    address: '',
                };
            });
        };

        const handleEditButton = (row: SensorInfo) => {
            dialogFormVisible.value = true;
            nextTick(() => {
                formRef.value?.resetFields();
                form.value = {
                    add_update: Operation.update,
                    _id: row._id,
                    company_code: row.company_code,
                    sensor_type: row.sensor_type,
                    address: row.address,
                };
            });
        };

        const handleDeleteButton = (row: SensorInfo) => {
            ElMessageBox.confirm('削除しますか？', '削除', {
                confirmButtonText: '削除',
                cancelButtonText: 'キャンセル',
                type: 'warning',
            }).then(() => {
                deleteSensor(row);
            }).catch(() => {
                // 取消删除
            });
        };

        const handleAddUpdateSubmit = async (formEl: FormInstance | undefined) => {
            if (!formEl) return
            await formEl.validate((valid) => {
                if (valid) {
                    if (form.value.add_update === Operation.add) {
                        addSensor();
                    } else if (form.value.add_update === Operation.update) {
                        updateSensor(form.value);
                    }
                    dialogFormVisible.value = false;
                }
            })
        }

        const handleImportButton = (file: UploadFile) => {
            if (file.raw) {
                const formData = new FormData()
                formData.append('file', file.raw)

                //文件过大 100K
                if (file.raw.size > 100 * 1024) {
                    ElMessage.error('ファイルが大きすぎます。');
                    return;
                }
                axios.post('/api/sensors/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then((res: AxiosResponse<ResponseData>) => {
                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        ElMessage.success(res.data.message);
                    } else {
                        const formattedErrorString = res.data.message?.replace(/\n/g, '<br>');

                        ElMessage.error({
                            message: formattedErrorString,
                            dangerouslyUseHTMLString: true
                        });
                    }
                    getSensorList();
                }).catch((error) => {
                    ElMessage.error(getErrorMessage(error as Error));
                })
            } else {
                ElMessage.error('ファイルが無効です')
            }
        }

        const handleSizeChange = (size: number) => {
            mPageSize.value = size;
            getSensorList();
        };
        const handleCurrentChange = (page: number) => {
            mCurrentPage.value = page;
            getSensorList();
        };

        /***** DB 操作 **************************/

        const apiCall = async (method: 'get' | 'post' | 'put' | 'delete', url: string, data?: any) => {
            try {
                loading.value = true;
                const res: AxiosResponse<ResponseData<SensorInfo[]>> = await axios({
                    method,
                    url,
                    data,
                    params: method === 'get' ? data : undefined, // Use params only for GET requests
                });

                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    if (method !== 'get') {
                        ElMessage.success(res.data.message);
                    }
                    return res.data;
                } else {
                    ElMessage.error(res.data.message);
                    return null;
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(error as Error));
            } finally {
                loading.value = false;
            }
        };

        const getSensorList = async () => {
            const data = await apiCall('get', '/api/sensors/search', {
                page: mCurrentPage.value,
                    limit: mPageSize.value,
                company_code: searchForm.value.company_code === ALL ? '' : searchForm.value.company_code,
            });
            if (data) {
                const { total, totalPages, currentPage, data: sensorData } = data;
                mCurrentPage.value = currentPage!;
                mTotalPages.value = totalPages!;
                mTotalRecords.value = total!;
                mTableData.value = sensorData!;
            }
        };

        const addSensor = async () => {
            await apiCall('post', '/api/sensors', form.value);
            getSensorList();
        };

        const updateSensor = async (row: SensorInfo) => {
            await apiCall('put', `/api/sensors/${row._id}`, row);
            getSensorList();
        };

        const deleteSensor = async (row: SensorInfo) => {
            await apiCall('delete', `/api/sensors/${row._id}`);
            getSensorList();
        };

        return {
            getFlag,
            searchForm,
            form,
            dialogFormVisible,
            loading,
            mTableData,
            mCurrentPage,
            mPageSize,
            mTotalPages,
            mTotalRecords,
            handleSearchButton,
            handleAddButton,
            handleEditButton,
            handleImportButton,
            handleDeleteButton,
            handleSizeChange,
            handleCurrentChange,
            handleAddUpdateSubmit,
            formRef,
            rules,
            loginUserRole,
            ROLES,
        }
    }
});
</script>